import { saveAs } from 'file-saver';
import docStyles from './docStyles';

const replaceTagName = (element: Element, newTagName: string, appendSpace: boolean = false) => {
  const newElement = document.createElement(newTagName);

  Array.from(element.attributes).forEach((attr) => {
    newElement.setAttribute(attr.name, attr.value);
  });

  while (element.firstChild) {
    newElement.appendChild(element.firstChild);
  }

  element.parentNode?.replaceChild(newElement, element);

  if (appendSpace && newElement.parentNode) {
    newElement.parentNode.insertBefore(document.createTextNode(' '), newElement.nextSibling);
  }
};

const replaceChildrenTagToTag = (
  elements: HTMLCollectionOf<Element> | NodeListOf<Element>,
  tagFrom: string,
  newTagName: string,
  appendSpace: boolean = false,
) => {
  Array.from(elements).forEach((container) => {
    const targetElements = container.querySelectorAll(tagFrom);
    Array.from(targetElements).forEach((el) => replaceTagName(el, newTagName, appendSpace));
  });
};

const maxWidth = 600;

const scaleTable = (table: HTMLTableElement) => {
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.visibility = 'hidden';
  container.style.width = 'auto';
  container.appendChild(table.cloneNode(true));
  document.body.appendChild(container);

  const originalWidth = container.offsetWidth;

  if (originalWidth > maxWidth) {
    table.style.width = '100%';
    table.style.maxWidth = '100%';
    table.removeAttribute('width');
    const tableCells = table.querySelectorAll('td, th');
    tableCells.forEach((cell) => {
      (cell as HTMLElement).style.wordBreak = 'break-word';
      (cell as HTMLElement).style.whiteSpace = 'normal';
      (cell as HTMLElement).style.padding = '2px';
    });
  }

  document.body.removeChild(container);
};

const scaleAllTables = (document: Document) => {
  const tables = document.querySelectorAll('table:not(.simulated-list-item)');
  tables.forEach((table) => scaleTable(table as HTMLTableElement));
};

const convertListsToWordLayout = (doc: Document): void => {
  doc.querySelectorAll('ol, ul').forEach((list) => {
    let hasWrappers = true;
    while (hasWrappers) {
      hasWrappers = false;
      // eslint-disable-next-line
			Array.from(list.children).forEach((child) => {
        if (child.tagName === 'DIV' || child.tagName === 'SPAN') {
          while (child.firstChild) {
            list.insertBefore(child.firstChild, child);
          }
          child.remove();
          hasWrappers = true;
        }
      });
    }
  });

  const isWrapper = (el: Element): boolean => {
    const tagOk = el.tagName === 'DIV' || el.tagName === 'SPAN';
    if (!tagOk) return false;

    const cls = (el.getAttribute('class') || '').toLowerCase();

    if (
      cls.includes('is-renvoi') ||
				cls.includes('is-full-renvoi') ||
				cls.includes('is-parent-full-renvoi') ||
				cls.includes('renvoi') ||
				cls.includes('reference-container')
    ) {
      return false;
    }

    if (
      el.hasAttribute('data-is-renvoi') ||
				el.hasAttribute('data-is-full-renvoi') ||
				el.hasAttribute('data-is-parent-full-renvoi') ||
				el.hasAttribute('data-reference-text')
    ) {
      return false;
    }

    return true;
  };

  const unwrapChildren = (parent: Element): void => {
    let hasWrappers = true;
    while (hasWrappers) {
      hasWrappers = false;
      const children = Array.from(parent.children);
      for (const child of children) {
        if (isWrapper(child)) {
          while (child.firstChild) parent.insertBefore(child.firstChild, child);
          child.remove();
          hasWrappers = true;
        }
      }
    }
  };

  Array.from(doc.querySelectorAll('ol, ul, li')).forEach(unwrapChildren);

  const toRoman = (num: number): string => {
    const lookup: [string, number][] = [
      ['m', 1000], ['cm', 900], ['d', 500], ['cd', 400],
      ['c', 100], ['xc', 90], ['l', 50], ['xl', 40],
      ['x', 10], ['ix', 9], ['v', 5], ['iv', 4], ['i', 1],
    ];
    let roman = '';
    for (const [letter, value] of lookup) {
      while (num >= value) {
        roman += letter;
        num -= value;
      }
    }
    return roman;
  };

  const toAlpha = (num: number): string => {
    let alpha = '';
    let temp = num;
    while (temp > 0) {
      temp--;
      alpha = String.fromCharCode(97 + (temp % 26)) + alpha;
      temp = Math.floor(temp / 26);
    }
    return alpha;
  };

  const getMarker = (list: Element, li: Element, index: number, depth: number): string => {
    const typeAttr = li.getAttribute('type') || list.getAttribute('type');
    if (typeAttr === 'A') return `${toAlpha(index).toUpperCase()}.`;
    if (typeAttr === 'a') return `${toAlpha(index).toLowerCase()}.`;
    if (typeAttr === 'I') return `${toRoman(index).toUpperCase()}.`;
    if (typeAttr === 'i') return `${toRoman(index).toLowerCase()}.`;
    if (typeAttr === '1') return `${index}.`;

    const styleTypes = [
      (li as HTMLElement).style.listStyleType,
      (list as HTMLElement).style.listStyleType,
      li.getAttribute('data-list-style-type'),
      list.getAttribute('data-list-style-type'),
    ];

    let typeToUse = '';
    for (const st of styleTypes) {
      if (st && st !== 'none' && st !== 'inherit' && st !== 'initial') {
        typeToUse = st.toLowerCase().replace(/['"]/g, '').trim();
        break;
      }
    }

    const cls = ((list.getAttribute('class') || '') + ' ' + (li.getAttribute('class') || '')).toLowerCase();

    if (!typeToUse) {
      if (cls.match(/\bupper-alpha\b|\btype-a\b/)) typeToUse = 'upper-alpha';
      else if (cls.match(/\blower-alpha\b/)) typeToUse = 'lower-alpha';
      else if (cls.match(/\bupper-roman\b/)) typeToUse = 'upper-roman';
      else if (cls.match(/\blower-roman\b/)) typeToUse = 'lower-roman';
      else if (cls.match(/\bdecimal\b/)) typeToUse = 'decimal';
      else if (cls.match(/\bcircle\b/)) typeToUse = 'circle';
      else if (cls.match(/\bsquare\b/)) typeToUse = 'square';
      else if (cls.match(/\bdisc\b/)) typeToUse = 'disc';
    }

    if (typeToUse === 'upper-alpha' || typeToUse === 'upper-latin') return `${toAlpha(index).toUpperCase()}.`;
    if (typeToUse === 'lower-alpha' || typeToUse === 'lower-latin') return `${toAlpha(index).toLowerCase()}.`;
    if (typeToUse === 'upper-roman') return `${toRoman(index).toUpperCase()}.`;
    if (typeToUse === 'lower-roman') return `${toRoman(index).toLowerCase()}.`;
    if (typeToUse === 'decimal') return `${index}.`;
    if (typeToUse === 'circle') return '○';
    if (typeToUse === 'square') return '■';
    if (typeToUse === 'dash' || typeToUse === '-') return '-';
    if (typeToUse === 'disc' && list.tagName !== 'OL') return '•';

    if (list.tagName === 'OL') {
      if (depth === 1) return `${index}.`;
      if (depth === 2) return `${toAlpha(index).toLowerCase()}.`;
      if (depth >= 3) return `${toRoman(index).toLowerCase()}.`;
      return `${index}.`;
    }

    if (depth === 1) return '•';
    if (depth === 2) return '○';
    return '■';
  };

  const estimateMarkerWidthPt = (marker: string) => {
    const len = marker.replace(/\s+/g, '').length;
    const w = 6 * Math.max(1, len) + 4;
    return Math.min(56, Math.max(12, w));
  };

  const lists = Array.from(doc.querySelectorAll('ol, ul'));
  const listDepths = new Map<Element, number>();

  lists.forEach((list) => {
    let depth = 1;
    let parent = list.parentElement;
    while (parent) {
      if (parent.tagName === 'OL' || parent.tagName === 'UL') depth++;
      parent = parent.parentElement;
    }
    listDepths.set(list, depth);
  });

  lists.reverse().forEach((list) => {
    const depth = listDepths.get(list) || 1;

    const startAttr = list.getAttribute('start');
    const valueAttr = list.getAttribute('value');
    let counter = startAttr ? parseInt(startAttr, 10) : (valueAttr ? parseInt(valueAttr, 10) : 1);

    const liChildren = Array.from(list.children).filter((c) => c.tagName === 'LI') as Element[];

    let tmpCounter = counter;
    const markers: string[] = [];
    liChildren.forEach((li) => {
      const liValue = li.getAttribute('value');
      if (liValue) tmpCounter = parseInt(liValue, 10);
      markers.push(getMarker(list, li, tmpCounter, depth));
      tmpCounter++;
    });

    const bulletColPt = markers.reduce((acc, m) => Math.max(acc, estimateMarkerWidthPt(m)), 12);
    const gapRightPt = 8;
    const indentPt = depth * 18;

    const listWrapper = doc.createElement('div');
    listWrapper.style.cssText = 'display:block !important;margin:0 !important;padding:0 !important;';

    let markerIndex = 0;

    Array.from(list.children).forEach((child) => {
      if (child.tagName === 'LI') {
        const liValue = child.getAttribute('value');
        if (liValue) counter = parseInt(liValue, 10);

        const marker = markers[markerIndex] || getMarker(list, child, counter, depth);
        markerIndex++;

        const liWrapper = doc.createElement('table');
        liWrapper.className = 'simulated-list-item';
        liWrapper.setAttribute('role', 'presentation');
        liWrapper.setAttribute('cellpadding', '0');
        liWrapper.setAttribute('cellspacing', '0');

        liWrapper.style.cssText = `
          width:100% !important;
          table-layout:fixed !important;
          border-collapse:collapse !important;
          margin:0 0 6pt 0 !important;
          margin-left:${indentPt}pt !important;
          mso-table-lspace:0pt !important;
          mso-table-rspace:0pt !important;
          mso-table-left:0pt !important;
          mso-table-top:0pt !important;
          mso-table-layout-alt:fixed !important;
          color:#000000 !important;
          font-size:12pt !important;
        `;

        const colgroup = doc.createElement('colgroup');
        const colBullet = doc.createElement('col');
        colBullet.style.width = `${bulletColPt}pt`;
        const colContent = doc.createElement('col');
        colContent.style.width = '100%';
        colgroup.appendChild(colBullet);
        colgroup.appendChild(colContent);
        liWrapper.appendChild(colgroup);

        const tbody = doc.createElement('tbody');
        const tr = doc.createElement('tr');
        tr.style.cssText = 'page-break-inside:avoid;color:#000000 !important;';

        const labelCell = doc.createElement('td');
        labelCell.setAttribute('valign', 'top');
        labelCell.style.cssText = `
          width:${bulletColPt}pt !important;
          vertical-align:top !important;
          text-align:left !important;
          border:none !important;
          padding:0 !important;
          mso-padding-alt:0pt ${gapRightPt}pt 0pt 0pt !important;
          white-space:nowrap !important;
          color:#000000 !important;
        `;
        labelCell.textContent = marker;

        const contentCell = doc.createElement('td');
        contentCell.setAttribute('valign', 'top');
        contentCell.style.cssText = `
          width:100% !important;
          vertical-align:top !important;
          text-align:left !important;
          border:none !important;
          padding:0 !important;
          mso-padding-alt:0pt 0pt 0pt 0pt !important;
          word-break:normal !important;
          white-space:normal !important;
          color:#000000 !important;
        `;

        const liRenvoiClasses = Array.from(child.classList).filter(
          (c) => c === 'is-full-renvoi' || c === 'is-renvoi' || c === 'is-parent-full-renvoi',
        );

        liRenvoiClasses.forEach((c) => {
          liWrapper.classList.add(c);
          contentCell.classList.add(c);
        });

        while (child.firstChild) contentCell.appendChild(child.firstChild);

        Array.from(contentCell.querySelectorAll('*')).forEach((el) => {
          if (el instanceof HTMLElement) {
            el.style.setProperty('margin', '0', 'important');
            el.style.setProperty('padding', '0', 'important');
            el.style.setProperty('text-indent', '0', 'important');
            el.style.setProperty('text-align', 'left', 'important');
          }
        });

        tr.appendChild(labelCell);
        tr.appendChild(contentCell);
        tbody.appendChild(tr);
        liWrapper.appendChild(tbody);

        listWrapper.appendChild(liWrapper);

        counter++;
      } else {
        listWrapper.appendChild(child);
      }
    });

    list.parentNode?.replaceChild(listWrapper, list);
  });
};

const dropEmptyListItems = (doc: Document) => {
  doc.querySelectorAll('li').forEach((li) => {
    const text = li.textContent?.replace(/\s+/g, '').trim();
    const hasMeaningfulChildren = !!li.querySelector('img,table,hr,br,ol,ul');
    if (!text && !hasMeaningfulChildren) li.remove();
  });
};

const sanitizeAlignment = (doc: Document) => {
  doc.querySelectorAll('*').forEach((node) => {
    if (node instanceof HTMLElement) {
      node.style.setProperty('text-align', 'left', 'important');
      const tagName = node.tagName.toLowerCase();
      if (!['td', 'th', 'table'].includes(tagName)) {
        node.style.setProperty('margin-left', '0', 'important');
        node.style.setProperty('padding-left', '0', 'important');
        node.style.setProperty('text-indent', '0', 'important');
      }
    }
  });
};

const applyRenvoiMode = (doc: Document, renvoiMode?: string) => {
  const unwrapTagKeepingChildren = (el: Element) => {
    const parent = el.parentNode;
    if (!parent) return;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    el.remove();
  };

  const hasLineThroughStyle = (el: Element) => {
    const style = (el as HTMLElement).getAttribute('style') || '';
    return /line-through/i.test(style);
  };

  if (renvoiMode === 'strikethrough') {
    doc.querySelectorAll('.is-full-renvoi, .is-full-renvoi *, .is-parent-full-renvoi, .is-parent-full-renvoi *').forEach((el) => {
      if (el instanceof HTMLElement) {
        el.style.setProperty('text-decoration', 'line-through', 'important');
      }
    });
    return;
  }

  if (renvoiMode === 'removed') {
    doc.querySelectorAll('.is-full-renvoi, .is-parent-full-renvoi').forEach((el) => el.remove());
    Array.from(doc.querySelectorAll('s')).forEach((s) => s.remove());
    Array.from(doc.querySelectorAll('*')).forEach((el) => {
      if (hasLineThroughStyle(el)) el.remove();
    });
    doc.querySelectorAll('p, span, div').forEach((el) => {
      const text = el.textContent?.replace(/\s+/g, '').trim();
      const hasChildren = !!el.querySelector('*');
      if (!text && !hasChildren) el.remove();
    });
    return;
  }

  if (renvoiMode === 'normal') {
    Array.from(doc.querySelectorAll('s')).forEach((s) => unwrapTagKeepingChildren(s));
    Array.from(doc.querySelectorAll('*')).forEach((el) => {
      if (hasLineThroughStyle(el)) (el as HTMLElement).style.setProperty('text-decoration', 'none', 'important');
    });
  }
};

const normalizeStrikethroughInsideSimulatedLists = (doc: Document) => {
  const nodes = Array.from(doc.querySelectorAll('.simulated-list-item s'));
  nodes.forEach((sNode) => {
    const span = doc.createElement('span');
    span.setAttribute('style', 'text-decoration: line-through;');
    while (sNode.firstChild) span.appendChild(sNode.firstChild);
    sNode.parentNode?.replaceChild(span, sNode);
  });
};

const propagateRenvoiClasses = (doc: Document) => {
  const renvoiClasses = ['is-full-renvoi', 'is-parent-full-renvoi'];

  renvoiClasses.forEach((cls) => {
    doc.querySelectorAll(`.${cls}`).forEach((el) => {
      el.querySelectorAll('ol, ul, li').forEach((descendant) => {
        descendant.classList.add(cls);
      });
    });
  });
};

const enforceRenvoiModeInsideSimulatedLists = (doc: Document, renvoiMode?: string) => {
  const renvoiClasses = ['.is-full-renvoi', '.is-parent-full-renvoi'];

  if (renvoiMode === 'strikethrough') {
    const selectors = renvoiClasses.flatMap((cls) => [
      `.simulated-list-item${cls} td`,
      `.simulated-list-item ${cls}`,
      `.simulated-list-item${cls} *`,
    ]).join(', ');

    if (selectors) {
      doc.querySelectorAll(selectors).forEach((el) => {
        if (el instanceof HTMLElement) {
          el.style.setProperty('text-decoration', 'line-through', 'important');
        }
      });
    }
    return;
  }

  if (renvoiMode === 'normal') {
    doc.querySelectorAll('.simulated-list-item td[style*="line-through"], .simulated-list-item span[style*="line-through"], .simulated-list-item *[style*="line-through"]').forEach((el) => {
      (el as HTMLElement).style.setProperty('text-decoration', 'none', 'important');
    });
    return;
  }

  if (renvoiMode === 'removed') {
    const selectors = renvoiClasses.map((cls) => `.simulated-list-item${cls}`).join(', ');
    if (selectors) {
      doc.querySelectorAll(selectors).forEach((el) => el.remove());
    }
  }
};

export const downloadDocx = (renvoiMode?: string) => {
  const header = `
    <html>
      <head>
        <meta charset="utf-8">
        <style>
          ${docStyles}
        </style>
      </head>
      <body>
  `;

  const footer = '</body></html>';

  const liveDocumentElement = window.document.getElementById('document');
  if (!liveDocumentElement) return;

  const clone = liveDocumentElement.cloneNode(true) as HTMLElement;

  const liveNodes = Array.from(liveDocumentElement.querySelectorAll('*'));
  const cloneNodes = Array.from(clone.querySelectorAll('*'));

  liveNodes.forEach((liveNode, i) => {
    const cloneNode = cloneNodes[i] as HTMLElement;
    if (!cloneNode) return;

    if (['OL', 'UL', 'LI'].includes(liveNode.tagName)) {
      const style = window.getComputedStyle(liveNode);
      const listStyleType = style.listStyleType;
      if (listStyleType && listStyleType !== 'none') {
        cloneNode.setAttribute('data-list-style-type', listStyleType);
      }
    }
  });

  clone.querySelectorAll('.hidden').forEach((el) => el.remove());

  const documentElementHtml = clone.outerHTML.toString()
    .replaceAll('<del>', '<s>')
    .replaceAll('</del>', '</s>')
    .replaceAll('<p></p>', '')
    .replaceAll('<br>', '<br/>');

  const fakeHtml = header + documentElementHtml + footer;

  const parser = new DOMParser();
  const fakeDocument = parser.parseFromString(fakeHtml, 'text/html');
  fakeDocument.querySelectorAll('ins').forEach((el) => replaceTagName(el, 'u', false));

  propagateRenvoiClasses(fakeDocument);

  applyRenvoiMode(fakeDocument, renvoiMode);

  dropEmptyListItems(fakeDocument);
  scaleAllTables(fakeDocument);
  convertListsToWordLayout(fakeDocument);
  normalizeStrikethroughInsideSimulatedLists(fakeDocument);
  enforceRenvoiModeInsideSimulatedLists(fakeDocument, renvoiMode);
  sanitizeAlignment(fakeDocument);

  ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].forEach((tagName) => {
    replaceChildrenTagToTag(fakeDocument.getElementsByTagName(tagName), 'p', 'span', true);
  });

  replaceChildrenTagToTag(fakeDocument.getElementsByClassName('reference-container'), 'div', 'span');

  Array.from(fakeDocument.getElementsByClassName('reference-container')).forEach((element) => {
    replaceTagName(element, 'span');
  });

  replaceChildrenTagToTag(fakeDocument.querySelectorAll('div:has(> .reference-container)'), 'p', 'span');

  const blob = new Blob(['\ufeff', fakeDocument.documentElement.outerHTML], { type: 'text/html' });
  saveAs(blob, 'cla.html');
};